/**
 * OCR Fallback for CAD-Generated PDFs
 *
 * CAD tools (AutoCAD/Revit) often "explode" fonts into vector paths —
 * characters look like text visually but are drawn as lines/curves.
 * pdf.js getTextContent() only extracts real text objects, missing these.
 *
 * This module uses Tesseract.js to OCR a rendered page image and extract
 * text with bounding box positions, converting pixel coords to PDF points.
 */

// Lazy-loaded: tesseract.js is ~3MB, only needed when user clicks "Run OCR"
type TessWorker = import('tesseract.js').Worker;
import { normalizeLabel, classifyLabel } from '@/domain/layout/classify';
import type { LayoutTextEntity, ClassificationPattern } from '@/domain/layout/types';

export interface OcrTextItem {
  text: string;
  /** Center X in PDF points (origin bottom-left) */
  cx: number;
  /** Center Y in PDF points (origin bottom-left) */
  cy: number;
  /** Width in PDF points */
  width: number;
  /** Height in PDF points */
  height: number;
  /** OCR confidence 0-100 */
  confidence: number;
}

/**
 * Preprocess canvas for better OCR: convert to high-contrast grayscale.
 * CAD drawings have thin lines on white background — boosting contrast
 * makes small text more legible to Tesseract.
 */
function preprocessForOcr(canvas: HTMLCanvasElement): void {
  const ctx = canvas.getContext('2d')!;
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = imageData.data;

  for (let i = 0; i < data.length; i += 4) {
    // Convert to grayscale
    const gray = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    // Boost contrast: push darks darker, lights lighter
    // Simple threshold-based: anything below 180 becomes black-ish, above becomes white
    const enhanced = gray < 180 ? Math.max(0, gray * 0.5) : Math.min(255, gray * 1.2);
    data[i] = data[i + 1] = data[i + 2] = enhanced;
  }

  ctx.putImageData(imageData, 0, 0);
}

/**
 * Run Tesseract.js OCR on a canvas and return text items in PDF point space.
 */
/**
 * SELF-HOSTED tesseract.js assets, deliberately. The library's defaults fetch
 * the worker, the WASM core and the language data (~15MB) from cdn.jsdelivr.net
 * and tessdata.projectnaptha.com at runtime — no SRI, and dead on restricted
 * client networks. Tolerable while browser OCR was an unreachable fallback;
 * since the AWS OCR Lambda's retirement (2026-09-06) this is the ONLY OCR
 * path, so everything ships in our own bundle under public/tesseract/:
 * worker.min.js, the three -lstm core variants the SIMD-tier picker chooses
 * from, and lang/eng.traineddata.gz. Version-locked to the installed
 * tesseract.js — re-copy from node_modules when bumping the package.
 * layoutOcr.selfhost.test.ts asserts these paths and the vendored files agree.
 */
export const TESSERACT_SELF_HOSTED = {
  workerPath: '/tesseract/worker.min.js',
  corePath: '/tesseract',
  langPath: '/tesseract/lang',
} as const;

export async function runOcrOnCanvas(
  canvas: HTMLCanvasElement,
  pageWidthPt: number,
  pageHeightPt: number,
  onProgress?: (percent: number, message: string) => void,
): Promise<OcrTextItem[]> {
  onProgress?.(2, 'Preprocessing image...');

  // Preprocess for better OCR on CAD drawings
  preprocessForOcr(canvas);

  onProgress?.(5, 'Initializing OCR engine...');

  // Dynamic import: tesseract.js (~3MB) is only loaded when OCR is actually needed
  const { createWorker, PSM } = await import('tesseract.js');

  const worker: TessWorker = await createWorker('eng', undefined, {
    ...TESSERACT_SELF_HOSTED,
    logger: (m) => {
      if (m.status === 'recognizing text') {
        const pct = Math.round(10 + m.progress * 80); // 10-90%
        onProgress?.(pct, `Recognizing text... ${Math.round(m.progress * 100)}%`);
      }
    },
  });

  try {
    // Use SPARSE_TEXT mode — designed for text scattered across an image
    // (not laid out in columns/paragraphs like a document)
    await worker.setParameters({
      tessedit_pageseg_mode: PSM.SPARSE_TEXT,
    });

    onProgress?.(10, 'Running OCR...');
    // Must explicitly request blocks output to get word-level bounding boxes
    const { data } = await worker.recognize(canvas, {}, { blocks: true, text: true });

    onProgress?.(92, 'Processing results...');

    const scaleX = pageWidthPt / canvas.width;
    const scaleY = pageHeightPt / canvas.height;

    const items: OcrTextItem[] = [];

    // Words are nested: Page → blocks → paragraphs → lines → words
    const blocks = data.blocks ?? [];
    for (const block of blocks) {
      for (const para of block.paragraphs) {
        for (const line of para.lines) {
          for (const word of line.words) {
            // Skip low-confidence results
            if (word.confidence < 30) continue;
            // Skip very short text
            const text = word.text.trim();
            if (text.length < 2) continue;

            const bbox = word.bbox;
            const pxW = bbox.x1 - bbox.x0;
            const pxH = bbox.y1 - bbox.y0;

            // Convert pixel coordinates to PDF point space
            // OCR bbox is top-left origin; PDF is bottom-left origin
            const ptW = pxW * scaleX;
            const ptH = pxH * scaleY;
            const ptLeft = bbox.x0 * scaleX;
            const ptTop = pageHeightPt - (bbox.y0 * scaleY); // flip Y

            items.push({
              text,
              cx: ptLeft + ptW / 2,
              cy: ptTop - ptH / 2,
              width: ptW,
              height: ptH,
              confidence: word.confidence,
            });
          }
        }
      }
    }

    onProgress?.(98, `Found ${items.length} text items`);
    return items;
  } finally {
    await worker.terminate();
  }
}

// ── Server-side OCR via the COG container (native Tesseract) ─────────────────

import { apiFetch, apiFetchRaw } from './apiFetch';
import { uploadAsset, scheduleAssetDeletion } from './assetApi';

const OCR_POLL_INTERVAL_MS = 1500;
const OCR_POLL_TIMEOUT_MS = 10 * 60 * 1000;

interface OcrDoneMarker {
  ok: boolean;
  items?: OcrTextItem[];
  tiles?: number;
  elapsed?: number;
  error?: string;
}

interface OcrProgressMarker {
  phase: string;
  done: number;
  total: number;
}

/** Asset-route JSON fetch: null on 404 (marker not written yet). */
async function fetchJobJson<T>(outPrefix: string, name: string): Promise<T | null> {
  const res = await apiFetchRaw(`/api/v2/assets/${outPrefix}${name}`, { method: 'GET' });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`${name}: HTTP ${res.status}`);
  return await res.json() as T;
}

/**
 * Run OCR server-side in the COG container (native Tesseract, tiled).
 *
 * Uploads the ~5MB PDF itself — NOT a 40-megapixel canvas render — and the
 * container renders the page at 200 DPI in tiles, OCRing each with real
 * per-tile progress (the browser path's misleading stuck-at-100% was an
 * owner complaint from the first live run). Items come back in the same
 * PDF-point / y-up / center contract as runOcrOnCanvas.
 */
export async function runOcrServer(
  pdfArrayBuffer: ArrayBuffer,
  pageIndex: number,
  pageWidthPt: number,
  pageHeightPt: number,
  siteId: string,
  onProgress?: (percent: number, message: string) => void,
): Promise<OcrTextItem[]> {
  onProgress?.(3, 'Uploading PDF for server OCR...');
  const upload = await uploadAsset(
    new Blob([pdfArrayBuffer], { type: 'application/pdf' }),
    siteId,
    'ocr_src',
    `ocr_${Date.now()}.pdf`,
  );

  try {
    onProgress?.(8, 'Dispatching OCR job...');
    const { outPrefix } = await apiFetch<{ jobId: string; outPrefix: string }>(
      `/api/v2/sites/${siteId}/ocr`,
      {
        method: 'POST',
        body: JSON.stringify({
          srcKey: upload.r2Key,
          pageIndex,
          // 150 ≈ the browser path's proven effective resolution; 200 was
          // measured 2-4x slower per tile on dense CAD sheets for no
          // recall gain (p2b1-33 tuning, Zmorot E131).
          dpi: 150,
          pageWidthPt,
          pageHeightPt,
        }),
      },
    );

    const deadline = Date.now() + OCR_POLL_TIMEOUT_MS;
    while (Date.now() < deadline) {
      const done = await fetchJobJson<OcrDoneMarker>(outPrefix, 'done.json');
      if (done) {
        if (!done.ok || !done.items) {
          throw new Error(done.error || 'server OCR failed');
        }
        onProgress?.(96, `Server OCR found ${done.items.length} words in ${done.elapsed ?? '?'}s`);
        return done.items;
      }
      const prog = await fetchJobJson<OcrProgressMarker>(outPrefix, 'progress.json').catch(() => null);
      if (prog && prog.total > 0 && prog.phase === 'ocr') {
        onProgress?.(
          10 + Math.round((prog.done / prog.total) * 82),
          `OCR tile ${prog.done}/${prog.total} (server)...`,
        );
      } else {
        onProgress?.(9, 'Rendering page on server...');
      }
      await new Promise((r) => setTimeout(r, OCR_POLL_INTERVAL_MS));
    }
    throw new Error('server OCR timed out');
  } finally {
    // The uploaded PDF is a temp copy for this job only (the import's own
    // pdfAssetUrl upload is separate) — clean it up regardless of outcome.
    scheduleAssetDeletion(upload.url);
  }
}

/**
 * Convert OCR text items to LayoutTextEntity[], de-duplicating against existing entities.
 * Reuses the same normalizeLabel + classifyLabel pipeline as the PDF text extraction.
 */
export function ocrItemsToEntities(
  ocrItems: OcrTextItem[],
  existingEntities: LayoutTextEntity[],
  customPatterns?: ClassificationPattern[],
): LayoutTextEntity[] {
  const DEDUP_DISTANCE_PT = 10; // skip OCR items within 10pt of existing entity
  let counter = 0;
  const now = Date.now();

  const newEntities: LayoutTextEntity[] = [];

  for (const item of ocrItems) {
    // De-duplicate: skip if any existing entity is very close
    const tooClose = existingEntities.some((e) => {
      const dx = e.cx - item.cx;
      const dy = e.cy - item.cy;
      return Math.sqrt(dx * dx + dy * dy) < DEDUP_DISTANCE_PT;
    });
    if (tooClose) continue;

    const normalized = normalizeLabel(item.text);
    if (!normalized || normalized.length < 2) continue;

    const classification = classifyLabel(normalized, customPatterns) ?? undefined;

    newEntities.push({
      id: `ocr-${now}-${counter++}`,
      text: item.text,
      normalizedText: normalized,
      cx: item.cx,
      cy: item.cy,
      width: item.width,
      height: item.height,
      rotation: 0,
      fontSize: item.height * 0.8, // approximate
      classification,
    });
  }

  return newEntities;
}

/**
 * Count classified (non-infrastructure) entities to decide if OCR should be offered.
 */
export function countClassifiedLabels(entities: LayoutTextEntity[]): number {
  return entities.filter(
    (e) => e.classification && e.classification.level !== 'infrastructure',
  ).length;
}
